import {
  ActionType,
  createReducer,
  createStandardAction,
  getType
} from "typesafe-actions";
import { InventoryView, LensTheme } from "generated/graphql";
import { Dispatch, MiddlewareAPI } from "redux";
import { isNil, mapValues, omit, pick } from "lodash";
import * as yup from "yup";
import { InferType } from "yup";

export enum TABLE_COLUMN_STATE {
  SHOWN = "SHOWN",
  HIDDEN = "HIDDEN"
}

// LensTheme is undefined on initial lift when included in Auth0Provider (which it is currently)
export const THEME_DARK = "DARK"; // LensTheme.Dark;
export const THEME_LIGHT = "LIGHT"; // LensTheme.Light;
export const INVENTORY_MAP = "MAP";
export const INVENTORY_LIST = "LIST";
export const THEME_DEFAULT = THEME_LIGHT;
export const INVENTORY_DEFAULT = INVENTORY_LIST;

// TABLE_COLUMNS values must match fields in the GraphQL schema
export const TABLE_COLUMNS = {
  ACCOUNTS: {
    NAME: "name",
    SITES: "siteCount",
    ROOMS: "roomCount",
    DEVICES: "deviceCount",
    MEMBERS: "memberCount",
    ROLE: "role",
    TENANT_TYPE: "type"
  },
  AUDIT_LOGS: {
    USER: "username",
    TIME: "logTime",
    EVENT_TYPE: "eventType",
    EVENT_DETAILS: "eventDetails",
    LOGIN_TYPE: "loginType",
    IP_ADDRESS: "publicIpAddress",
    AUTHORIZED: "authorized"
  },
  CLARITI_ACCESS_TOKENS: {
    TOKEN: "token",
    CREATION_TIME: "creationTime",
    LAST_USED_TIME: "lastUsedTime"
  },
  CLARITI_AUDIT_EVENTS: {
    EVENT_TYPE: "eventTypeName",
    EVENT_TIME: "eventTime",
    USER_ID: "userId",
    ENTITLEMENT_NAME: "entitlementName",
    PRODUCT_NAME: "productName"
  },
  CLARITI_ENTITLEMENTS: {
    NAME: "name",
    TYPE: "type",
    VERSION: "version",
    ATTRIBUTE_COUNT: "attributeCount",
    ASSIGNED_ATTRIBUTES: "assignedCount",
    REMOVED_ATTRIBUTES: "removedCount",
    ACTIVATION_CODE: "activationCode",
    EXPIRES: "expires"
  },
  CLARITI_PRODUCT_VERSIONS: {
    NAME: "name",
    LATEST_VERSION: "latestVersion"
  },
  CLARITI_TRIAL_USERS: {
    EMAIL: "email",
    CONFERENCE_ROOM: "conferenceRoom",
    DATE_CREATED: "creationTime",
    EXPIRED: "expired",
    TOTAL_CALLS: "numberOfCalls",
    TOTAL_PARTICIPANTS: "totalNumberOfParticipants",
    MIN_PARTICIPANTS: "minNumberOfParticipants",
    MAX_PARTICIPANTS: "maxNumberOfParticipants",
    AVERAGE_PARTICIPANTS: "averageNumberOfParticipants",
    TOTAL_DURATION: "totalDuration"
  },
  DEVICE_GROUP_POLICIES: {
    DEVICE_MODEL: "deviceModel",
    DEVICE_COUNT: "deviceCount",
    SETTINGS_COUNT: "settingsCount"
  },
  DEVICE_GROUPS: {
    NAME: "name",
    DEVICE_COUNT: "deviceCount",
    MODEL_COUNT: "modelCount",
    CREATED_AT: "createdAt"
  },
  DEVICE_SERVICE_INFORMATION: {
    LINE_ID: "lineId",
    STATUS: "status",
    REGISTRATION_TYPE: "registrationType",
    DOMAIN: "domain",
    REGISTRATION_ADDRESS: "registrationAddress"
  },
  DEVICE_USER_GROUPS: {
    NAME: "name",
    USER_COUNT: "user_count",
    CREATED_AT: "created_at",
    LAST_MODIFIED: "last_modified",
    GROUP_TYPE: "group_type",
    POLICY_PRIORITY: "policy_priority"
  },
  DEVICE_USERS: {
    NAME: "name",
    PRIMARY_DEVICE: "primary_device",
    APP_VERSION: "softwareVersion",
    DEVICE_COUNT: "device_count",
    LAST_DETECTED: "last_detected",
    CLIENT: "client"
  },
  DEVICES: {
    CONNECTION: "connection",
    LINKED_DEVICES: "linkedDevices",
    PROVISIONING: "provisioning",
    LINES: "lines",
    NAME: "name",
    SERIAL_NUMBER: "serialNumber",
    IPV4: "internalIp",
    MAC_ADDRESS: "macAddress",
    SITE: "site",
    ROOM: "room",
    MODEL: "hardwareModel",
    VERSION: "softwareVersion",
    PROVIDER: "activeApplicationName",
    LAST_PROVISIONING_SYNC: "lastConfigRequestDate",
    DATE_REGISTERED: "dateRegistered",
    DATE_LAST_SEEN: "lastDetected",
    POLY_PLUS: "entitlements"
  },
  EXTERNAL_USER_GROUPS: {
    NAME: "name",
    DESCRIPTION: "description"
  },
  LICENSES: {
    LICENSE_KEY: "licenseKey",
    TYPE: "type",
    ACTIVATION_DATE: "activationDate",
    END_DATE: "endDate",
    QUANTITY: "quantity",
    USER_ID: "userId"
  },
  MEMBERS: {
    NAME: "name",
    EMAIL: "email",
    CREATED_AT: "created_at",
    LOGINS_COUNT: "logins_count",
    ROLE: "role"
  },
  PENDING_USERS: {
    NAME: "name",
    EMAIL: "email"
  },
  POLICIES: {
    NAME: "name",
    DEVICES: "count",
    SETTINGS: "settings"
  },
  POLY_PLUS_ENTITLE_DEVICES: {
    CONNECTION: "connection",
    SERIAL_NUMBER: "serialNumber",
    SITE: "site",
    // SHIPMENT_DATE: "shipmentDate",
    DATE_REGISTERED: "dateRegistered"
  },
  RESOURCE_FILES: {
    FILE_NAME: "fileName",
    UPLOADED_TIME: "uploadedTime",
    UPLOADED_BY: "uploadedBy",
    FILE_SIZE: "fileSize"
  },
  ROOMS: {
    NAME: "name",
    SITE: "siteName",
    DEVICES: "deviceCount",
    PERCENT_ONLINE: "percentOnline",
    CAPACITY: "capacity",
    FLOOR: "floor"
  },
  SITES: {
    NAME: "name",
    ROOMS: "roomCount",
    DEVICES: "deviceCount",
    PERCENT_ONLINE: "percentOnline"
  },
  USER_DEVICES: {
    NAME: "name",
    SITE: "site",
    VERSION: "softwareVersion",
    CLIENT: "client",
    APP_VERSION: "appVersion",
    LAST_DETECTED: "lastDetected"
  }
};

const HIDDEN_COLUMNS = {
  CLARITI_AUDIT_EVENTS: {
    USER_NAME: "userName"
  },
  CLARITI_ENTITLEMENTS: {
    MAPPING_ID1: "mappingId1",
    MAPPING_ID2: "mappingId2",
    LICENSE_TYPE: "licenseType",
    ASSIGNMENT_TYPE: "assignmentType",
    ATTRIBUTE_TYPE: "attributeType",
    ASSIGNED_PRODUCT_COUNT: "assignedProductCount",
    AVAILABLE_ATTRIBUTES: "availableCount",
    PENDING_ATTRIBUTES: "pendingAssignmentCount",
    PENDING_ASSIGN_PRODUCT_COUNT: "pendingAssignmentProductCount",
    PENDING_RELEASE_ATTRIBUTES: "pendingReleaseCount",
    PENDING_RELEASE_PRODUCT_COUNT: "pendingReleaseProductCount",
    REMOVED_PRODUCT_COUNT: "removedProductCount",
    BEGINS: "begins",
    CREATION_TIME: "creationTime",
    LAST_MODIFIED_TIME: "lastModifiedTime"
  },
  DEVICE_SERVICE_INFORMATION: {
    USER_ID: "userId",
    SIP_PORT: "sipPort",
    DISPLAY_NAME: "displayName",
    EXPIRY: "expiry",
    LINE_TYPE: "lineType",
    REGISTERED_IP: "registeredIP",
    SERVER_PLATFORM: "srvPlatform",
    SIP_ADDRESS: "sipAddr",
    SIP_TRANSPORT: "sipTransport",
    OUTBOUND_PROXY_PORT: "outboundProxyPort",
    OUTBOUND_PROXY_TRANSPORT: "outboundProxyTransport",
    FAILURE_REASON: "failurereason"
  },
  DEVICES: {
    LAST_PROVISIONING_SYNC: "lastConfigRequestDate",
    DATE_REGISTERED: "dateRegistered",
    DATE_LAST_SEEN: "lastDetected",
    POLY_PLUS: "entitlements"
    // SHIPMENT_DATE: "shipmentDate"
  },
  POLY_PLUS_ENTITLE_DEVICES: {
    CONNECTION: "connection",
    SERIAL_NUMBER: "serialNumber",
    IPV4: "internalIp",
    MAC_ADDRESS: "macAddress",
    SITE: "site",
    ROOM: "room",
    MODEL: "hardwareModel",
    VERSION: "softwareVersion",
    PROVIDER: "activeApplicationName",
    LAST_PROVISIONING_SYNC: "lastConfigRequestDate",
    DATE_LAST_SEEN: "lastDetected",
    POLY_PLUS: "entitlements"
  }
};

export const TABLES = {
  ACCOUNTS: {
    NAME: "ACCOUNTS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.ACCOUNTS
  },
  AUDIT_LOGS: {
    NAME: "AUDIT_LOGS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.AUDIT_LOGS
  },
  CLARITI_ACCESS_TOKENS: {
    NAME: "CLARITI_ACCESS_TOKENS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.CLARITI_ACCESS_TOKENS
  },
  CLARITI_AUDIT_EVENTS: {
    NAME: "CLARITI_AUDIT_EVENTS",
    HIDDEN: HIDDEN_COLUMNS.CLARITI_AUDIT_EVENTS,
    COLUMNS: TABLE_COLUMNS.CLARITI_AUDIT_EVENTS
  },
  CLARITI_ENTITLEMENTS: {
    NAME: "CLARITI_ENTITLEMENTS",
    HIDDEN: HIDDEN_COLUMNS.CLARITI_ENTITLEMENTS,
    COLUMNS: TABLE_COLUMNS.CLARITI_ENTITLEMENTS
  },
  CLARITI_PRODUCT_VERSIONS: {
    NAME: "CLARITI_PRODUCT_VERSIONS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.CLARITI_PRODUCT_VERSIONS
  },
  CLARITI_TRIAL_USERS: {
    NAME: "CLARITI_TRIAL_USERS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.CLARITI_TRIAL_USERS
  },
  DEVICE_GROUP_DEVICES: {
    NAME: "DEVICE_GROUP_DEVICES",
    HIDDEN: [],
    COLUMNS: pick(TABLE_COLUMNS.DEVICES, [
      "CONNECTION",
      "NAME",
      "SERIAL_NUMBER",
      "SITE",
      "MODEL",
      "VERSION"
    ])
  },
  DEVICE_GROUP_POLICIES: {
    NAME: "DEVICE_GROUP_POLICIES",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.DEVICE_GROUP_POLICIES
  },
  DEVICE_GROUPS: {
    NAME: "DEVICE_GROUPS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.DEVICE_GROUPS
  },
  DEVICE_SERVICE_INFORMATION: {
    NAME: "DEVICE_SERVICE_INFORMATION",
    HIDDEN: HIDDEN_COLUMNS.DEVICE_SERVICE_INFORMATION,
    COLUMNS: TABLE_COLUMNS.DEVICE_SERVICE_INFORMATION
  },
  DEVICE_USER_GROUPS: {
    NAME: "DEVICE_USER_GROUPS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.DEVICE_USER_GROUPS
  },
  DEVICE_USERS: {
    NAME: "DEVICE_USERS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.DEVICE_USERS
  },
  DEVICES: {
    NAME: "DEVICES",
    HIDDEN: HIDDEN_COLUMNS.DEVICES,
    COLUMNS: omit(TABLE_COLUMNS.DEVICES, [
      "LAST_PROVISIONING_SYNC",
      "DATE_REGISTERED",
      "DATE_LAST_SEEN",
      "POLY_PLUS"
    ])
  },
  EXTERNAL_USER_GROUPS: {
    NAME: "EXTERNAL_USER_GROUPS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.EXTERNAL_USER_GROUPS
  },
  LICENSES: {
    NAME: "LICENSES",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.LICENSES
  },
  MEMBERS: {
    NAME: "MEMBERS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.MEMBERS
  },
  PENDING_USERS: {
    NAME: "PENDING_USERS",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.PENDING_USERS
  },
  POLICIES: {
    NAME: "DEVICE_MODEL",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.POLICIES
  },
  POLY_PLUS_ENTITLE_DEVICES: {
    NAME: "POLY_PLUS_ENTITLE_DEVICES",
    // Include all columns EXCEPT "Lines"
    COLUMNS: TABLE_COLUMNS.POLY_PLUS_ENTITLE_DEVICES,
    // Hide all columns except default of SERIAL_NUMBER, SITE, and SHIPMENT_DATE
    HIDDEN: {
      ...omit(TABLE_COLUMNS.DEVICES, ["LINES", "SERIAL_NUMBER", "SITE"])
    }
  },
  RESOURCE_FILES: {
    NAME: "RESOURCE_FILES",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.RESOURCE_FILES
  },
  ROOM_DEVICES: {
    NAME: "ROOM_DEVICES",
    HIDDEN: [],
    COLUMNS: omit(TABLE_COLUMNS.DEVICES, [
      "ROOM",
      "SERIAL_NUMBER",
      "PROVIDER",
      "POLY_PLUS",
      "LINKED_DEVICES",
      "CONNECTION",
      "PROVISIONING"
    ])
  },
  ROOMS: {
    NAME: "ROOMS",
    HIDDEN: [],
    COLUMNS: omit(TABLE_COLUMNS.ROOMS, "FLOOR")
  },
  SITE_DEVICES: {
    NAME: "SITE_DEVICES",
    HIDDEN: [],
    COLUMNS: omit(TABLE_COLUMNS.DEVICES, [
      "SITE",
      "SERIAL_NUMBER",
      "PROVIDER",
      "POLY_PLUS",
      "LINKED_DEVICES",
      "CONNECTION",
      "PROVISIONING"
    ])
  },
  SITE_ROOMS: {
    NAME: "SITE_ROOMS",
    HIDDEN: pick(TABLE_COLUMNS.ROOMS, ["FLOOR"]),
    COLUMNS: omit(TABLE_COLUMNS.ROOMS, ["SITE", "FLOOR"])
  },
  SITES: {
    NAME: "SITES",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.SITES
  },
  USER_DEVICES: {
    NAME: "USER_DEVICES",
    HIDDEN: [],
    COLUMNS: TABLE_COLUMNS.USER_DEVICES
  }
};

// .required() from yup expects array to have at least one element, but for our use case empty array is perfectly valid
// custom test function is there to check if value is present (not undefined/null)
const tableColumnSchema = yup
  .array()
  .test("defined", "", value => !isNil(value))
  .of(yup.string().required());
const tableSchema = yup.object({
  [TABLE_COLUMN_STATE.SHOWN]: tableColumnSchema,
  [TABLE_COLUMN_STATE.HIDDEN]: tableColumnSchema
});

// since yup doesn't support map-like structures, this is one way to validate such objects
const tablesSchema = yup.lazy((obj: any) =>
  yup.object(mapValues(obj, () => tableSchema))
);

const enumSchema = <T>(values: T[]) => yup.mixed<T>().oneOf(values);

const sessionUserMetaSchema = yup.object({
  inventorySessionState: enumSchema<InventoryView>([
    INVENTORY_LIST,
    INVENTORY_MAP
  ] as InventoryView[])
});

const userMetaSchema = yup
  .object({
    acceptedToS: yup.string().nullable(),
    ackAdblockWarning: yup.string().nullable(),
    defaultInventoryView: enumSchema<InventoryView>([
      INVENTORY_LIST,
      INVENTORY_MAP
    ] as InventoryView[]),
    lastTenantId: yup.string().nullable(),
    subnetInstructions: yup.string().nullable(),
    theme: enumSchema<LensTheme>([THEME_LIGHT, THEME_DARK] as LensTheme[]),
    highContrastCharts: yup.boolean().nullable(),
    tables: tablesSchema
  })
  .concat(sessionUserMetaSchema);

type SessionUserMetaData = InferType<typeof sessionUserMetaSchema>;
type TableMetaData = {
  [tableName: string]: InferType<typeof tableSchema>;
};
export type UserMetaDataState = Omit<
  InferType<typeof userMetaSchema>,
  "tables"
> & {
  tables: TableMetaData;
};

const validateProperty = (
  state: unknown,
  property: keyof UserMetaDataState
): state is UserMetaDataState => {
  if (!state) {
    return false;
  }

  try {
    userMetaSchema.validateSyncAt(property, state as any, { strict: true });
  } catch (err) {
    return false;
  }

  return true;
};

/**
 * IMPORTANT:
 * The only function that should be called directly within this file
 * is the getUserMeta from the reducer.
 */

export const USER_META_KEY = "lensSettings";
export const getLocalStorage = (): Record<any, any> | undefined => {
  try {
    const localStorageState = localStorage.getItem(USER_META_KEY);
    if (!localStorageState) {
      return undefined;
    }

    return JSON.parse(localStorageState);
  } catch (err) {
    return undefined;
  }
};

export const saveLocalStorage = (state: UserMetaDataState) => {
  if (!state) {
    state = defaultUserMetaState;
  }

  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem(USER_META_KEY, serialized);
  } catch {
    // intentionally empty
  }
};

export const defaultSessionMetadata: SessionUserMetaData = {
  inventorySessionState: INVENTORY_LIST as InventoryView
};
export const defaultTableMetadata: TableMetaData = {
  ...Object.values(TABLES).reduce(
    (state, table) => ({
      ...state,
      [table.NAME]: {
        [TABLE_COLUMN_STATE.SHOWN]: Object.values(table.COLUMNS),
        [TABLE_COLUMN_STATE.HIDDEN]: Object.values(table.HIDDEN)
      }
    }),
    {}
  )
};

export const mergeTableStates = (
  defaultTableState: TableMetaData,
  userTableState?: TableMetaData
) => {
  if (!userTableState) {
    return defaultTableState;
  }

  // merge user table state with default, handling the cases of subsequent adding/removing columns
  let mergedTableState: TableMetaData = {};
  Object.entries(defaultTableState).forEach(([tableName, defaultState]) => {
    const userState = userTableState[tableName];
    if (userState) {
      const defaultColumnsShown = [...defaultState.SHOWN];
      const defaultColumnsHidden = [...defaultState.HIDDEN];
      const defaultColumnsSet = new Set([
        ...defaultColumnsShown,
        ...defaultColumnsHidden
      ]);

      // if there is column present in user state but not in default table state, remove it from user state
      const presentInDefaultState = (c: string) => defaultColumnsSet.has(c);
      let shownColumns = userState.SHOWN.filter(presentInDefaultState);
      let hiddenColumns = userState.HIDDEN.filter(presentInDefaultState);
      let allColumns = [...shownColumns, ...hiddenColumns];

      // similarly, if there is column present in the default table state but not in user state, add it to user state
      const userColumnsSet = new Set(allColumns);
      const missingShownColumns = defaultColumnsShown.filter(
        c => !userColumnsSet.has(c)
      );
      const missingHiddenColumns = defaultColumnsHidden.filter(
        c => !userColumnsSet.has(c)
      );
      shownColumns.push(...missingShownColumns);
      hiddenColumns.push(...missingHiddenColumns);

      mergedTableState[tableName] = {
        SHOWN: shownColumns,
        HIDDEN: hiddenColumns
      };
    } else {
      // set state to default since there isn't anything in user state
      mergedTableState[tableName] = defaultState;
    }
  });

  return mergedTableState;
};

export const defaultUserMetaState: UserMetaDataState = {
  acceptedToS: null,
  theme: THEME_DEFAULT as LensTheme,
  highContrastCharts: false,
  lastTenantId: null,
  ackAdblockWarning: null,
  subnetInstructions: null,
  defaultInventoryView: INVENTORY_DEFAULT as InventoryView,
  ...defaultSessionMetadata,
  tables: defaultTableMetadata
};

type SettingActionType =
  | "GET_USER_META"
  | "UPDATE_USER_META"
  | "UPDATE_SESSION_META"
  | "LOAD_USER_DATA_FROM_LOCALSTORAGE";

export const loadUserDataFromLocalStorage = createStandardAction<
  SettingActionType
>("LOAD_USER_DATA_FROM_LOCALSTORAGE")();
export const getUserMeta = createStandardAction<SettingActionType>(
  "GET_USER_META"
)();
export const updateUserMeta = createStandardAction<SettingActionType>(
  "UPDATE_USER_META"
)<UserMetaDataState>();

const UserMetaActions = { getUserMeta, updateUserMeta };
export type UserMetaAction = ActionType<typeof UserMetaActions>;

/**
 * DO NOT use the updateUserMeta action directly.
 * Updates to User Meta should be done via the useUserMeta hook, which
 * ensures that meta is stored in local storage as well as executing
 * the appropriate graphql query.
 */
export const UserMetaReducer = createReducer(defaultUserMetaState)
  .handleAction(loadUserDataFromLocalStorage, state => {
    const localStorageState = getLocalStorage();

    return (Object.keys(defaultUserMetaState) as Array<
      keyof typeof defaultUserMetaState
    >).reduce(
      (newState, key) => {
        switch (key) {
          case "tables":
            // validate each of the tables separately, instead of entire property
            // that way if state of one of the tables is not valid, others can still be used
            const tablesState =
              localStorageState &&
              (localStorageState as UserMetaDataState).tables;
            if (tablesState) {
              const userTableState = Object.entries(
                tablesState as TableMetaData
              ).reduce((tables, [name, state]) => {
                if (tableSchema.isValidSync(state)) {
                  tables[name] = state;
                }
                return tables;
              }, {} as TableMetaData);

              newState = {
                ...newState,
                tables: mergeTableStates(defaultTableMetadata, userTableState)
              };
            }
            break;
          case "defaultInventoryView":
            // populate "inventorySessionState" with the same value on initial load,
            // afterwards user can change that value during his session
            if (validateProperty(localStorageState, key)) {
              let value = localStorageState[key];
              newState = {
                ...newState,
                [key]: value,
                inventorySessionState: value
              };
            }
            break;
          case "inventorySessionState":
            // ignore this one on initial load, since it should be session only
            break;
          default:
            if (validateProperty(localStorageState, key)) {
              let value = localStorageState[key];
              newState = {
                ...newState,
                [key]: value
              };
            }
            break;
        }

        return newState;
      },
      { ...state }
    );
  })
  .handleAction(getUserMeta, state => ({
    ...state
  }))
  .handleAction(
    updateUserMeta,
    (state: UserMetaDataState, action: { payload: UserMetaDataState }) => {
      return {
        ...(action.payload ? action.payload : state)
      };
    }
  );

export const userMetaMiddleware = (store: MiddlewareAPI) => (
  next: Dispatch
) => (action: { type: string; payload: UserMetaDataState }) => {
  if (getType(updateUserMeta) === action.type) {
    saveLocalStorage(action.payload);
  }

  next(action);
};

export type ClientOptionType = { [x: string]: string[] };
